import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui-custom/Card";
import Button from "../ui-custom/Button";
import { Input } from "../ui-custom/Input";
import { Play, CheckCircle2, Clock, XCircle, Search, ChevronDown, ChevronUp, Eye, CheckCircle, PlayCircle, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { PettyCashApprovalDetailDialog } from "./PettyCashApprovalDetailDialog";

const statusTabs = [
  { id: "PENDING", label: "Pending", icon: Clock, color: "text-sm-accent", bg: "bg-sm-accent/10" },
  { id: "APPROVED", label: "Approved", icon: CheckCircle, color: "text-dark-green", bg: "bg-dark-green/10" },
  { id: "EXECUTED", label: "Executed", icon: PlayCircle, color: "text-sm-primary", bg: "bg-sm-primary/10" },
  { id: "REJECTED", label: "Rejected", icon: AlertCircle, color: "text-destructive", bg: "bg-destructive/10" },
];

const StatusBadge = ({ status }) => {
  const tab = statusTabs.find(t => t.id === status) || statusTabs[0];
  const Icon = tab.icon;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${tab.bg} ${tab.color} border-current/20`}>
      <Icon size={12} className="mr-1" />
      {tab.label}
    </span>
  );
};

export function PettyCashExecute({
  approvals,
  onExecute,
  executeLoadingById,
  loadingApprovals = false,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("PENDING");
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const filteredApprovals = useMemo(() => {
    return (approvals || []).filter(item => {
      const statusMatch = String(item.status_type || "PENDING").toUpperCase() === activeTab;
      const searchString = `${item?.site || ''} ${item?.category || ''} ${item?.payment_type || ''} ${item?.approval_id || ''}`.toLowerCase();
      return statusMatch && searchString.includes(searchQuery.toLowerCase());
    });
  }, [approvals, activeTab, searchQuery]);

  const handleView = (item) => {
    const approvalId = item?.approval_id ?? item?.id;
    const status = String(item.status_type || "PENDING").toUpperCase();

    const expenseLike = {
      id: approvalId,
      employeeName: item.requested_by?.split('|')[1]?.trim() || "You",
      title: item.category || "Approval Request",
      amount: Number(item.amount || 0),
      project: item.site != null ? `${item.site_name}` : "Organization",
      date: item.created_on || new Date().toISOString(),
      statusType: status,
      category: item.category,
      remarks: item.remarks,
      _raw: item,
    };

    setSelectedApproval(expenseLike);
    setIsDetailOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2 p-1 bg-muted/20 rounded-lg w-fit">
        {statusTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const count = (approvals || []).filter(a => String(a.status_type || "PENDING").toUpperCase() === tab.id).length;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${isActive
                ? "bg-white text-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-white/50"
                }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? tab.color : ""}`} />
              {tab.label}
              {count > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 text-[10px] rounded-full ${isActive ? tab.bg + " " + tab.color : "bg-muted text-muted-foreground"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded ${statusTabs.find(t => t.id === activeTab)?.bg}`}>
                {(() => {
                  const tabInfo = statusTabs.find(t => t.id === activeTab);
                  const Icon = tabInfo?.icon || Clock;
                  return <Icon className={`w-4 h-4 ${tabInfo?.color}`} />;
                })()}
              </div>
              <div>
                <CardTitle className="text-base font-semibold">
                  {statusTabs.find(t => t.id === activeTab)?.label} Requests
                </CardTitle>
                <p className="text-xs text-text-gray">
                  {loadingApprovals ? "Refreshing items..." : `${filteredApprovals.length} items found`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-gray" />
                <Input
                  placeholder="Search approvals..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 w-48"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {loadingApprovals && filteredApprovals.length === 0 && (
            <div className="text-center py-12 text-text-gray text-sm">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              Loading approvals...
            </div>
          )}

          {!loadingApprovals && filteredApprovals.map((item) => {
            const approvalId = item?.approval_id ?? item?.id;
            const date = item?.created_on || item?.approved_on || item?.date;

            return (
              <div key={approvalId} className="border border-sm-border rounded-lg p-4 bg-gray-bg/10 hover:bg-gray-bg/20 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 font-semibold text-xs border border-sm-border">
                      #{approvalId}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-primary-text">{item.category}</span>
                        <StatusBadge status={String(item.status_type || "PENDING").toUpperCase()} />
                        {item.status_type === 'APPROVED' && item.is_executed && (
                          <span className="flex items-center px-1.5 py-0.5 bg-green-50 text-green-600 rounded text-[10px] font-bold border border-green-200 uppercase tracking-tight">
                            <CheckCircle2 size={10} className="mr-1" /> Executed
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-text-gray flex items-center gap-2">
                        <span className="font-medium text-text-gray/80">{item.payment_type || "Expense"}</span>
                        <span>•</span>
                        <span>{item.site != null ? `${item.site_name}` : "Organization"}</span>
                        {date && (
                          <>
                            <span>•</span>
                            <span>{format(new Date(date), 'MMM d, yyyy • hh:mm a')}</span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-xs text-text-gray mb-0.5">Amount</p>
                      <p className="font-bold text-sm-primary text-lg leading-tight">₹{Number(item.amount || 0).toLocaleString("en-IN")}</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs font-medium"
                        onClick={() => handleView(item)}
                      >
                        <Eye size={14} className="mr-1.5" />
                        View
                      </Button>
                      {item.status_type === 'APPROVED' && !item.is_executed && (
                        <Button
                          size="sm"
                          onClick={() => onExecute(approvalId, item.category)}
                          disabled={Boolean(executeLoadingById?.[approvalId])}
                          className="bg-dark-green text-white hover:bg-dark-green/90 h-8 text-xs font-medium"
                        >
                          <Play size={12} className="mr-1.5 fill-current" />
                          {executeLoadingById?.[approvalId] ? "Executing..." : "Execute"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {!loadingApprovals && filteredApprovals.length === 0 && (
            <div className="text-center py-20 bg-muted/10 rounded-xl border border-dashed border-sm-border">
              <Clock className="w-10 h-10 text-text-gray/20 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-primary-text mb-1">No items found</h3>
              <p className="text-sm text-text-gray max-w-[250px] mx-auto">
                {searchQuery ? `No matches for "${searchQuery}" in ${activeTab.toLowerCase()} status.` : `There are no ${activeTab.toLowerCase()} requests at the moment.`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <PettyCashApprovalDetailDialog
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        approval={selectedApproval}
      />
    </div>
  );
}
